import { useMemo } from "react";
import {
	Chart as ChartJS,
	Legend,
	LegendItem,
	LinearScale,
	LineElement,
	PointElement,
	Tooltip,
	TooltipItem,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { DUTY_POINT_LABEL } from "../../../data/chart";
import { ChartPoint, ChartSeries } from "../../../types/types";
import {
	buildPumpCurveChartData,
	formatChartTick,
} from "../../../utils/helpers/chartHelpers";

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

type PumpCurveChartProps = {
	series: ChartSeries[];
	systemCurvePoints: ChartPoint[];
	dutyGpm: number | null;
	dutyTdh: number | null;
	hiddenSeries: ReadonlySet<string>;
};

export function PumpCurveChart({
	series,
	systemCurvePoints,
	dutyGpm,
	dutyTdh,
	hiddenSeries,
}: PumpCurveChartProps) {
	const { data, options } = useMemo(() => {
		const { datasets, xMax } = buildPumpCurveChartData(
			series,
			systemCurvePoints,
			dutyGpm,
			dutyTdh,
			hiddenSeries,
		);

		return {
			data: { datasets },
			options: {
				responsive: true,
				maintainAspectRatio: false,
				parsing: false as const,
				plugins: {
					legend: {
						position: "bottom" as const,
						// Curve visibility is driven by the per-series switches, so the
						// legend stays display-only instead of toggling datasets.
						onClick: () => undefined,
						labels: {
							filter: (item: LegendItem) =>
								item.text !== DUTY_POINT_LABEL,
						},
					},
					tooltip: {
						// Curves opt out of hover detection via pointHitRadius, so
						// this only ever runs for the duty point.
						filter: (item: TooltipItem<"line">) =>
							item.dataset.label === DUTY_POINT_LABEL,
						callbacks: {
							title: (items: TooltipItem<"line">[]) =>
								items[0]?.dataset.label ?? "",
							label: (item: TooltipItem<"line">) => {
								const { x, y } = item.parsed;
								if (x === null || y === null) {
									return "";
								}

								return `${formatChartTick(x)} GPM @ ${formatChartTick(y)} TDH`;
							},
						},
					},
				},
				scales: {
					x: {
						type: "linear" as const,
						title: {
							display: true,
							text: "Flow (GPM)",
						},
						min: 0,
						max: xMax,
						ticks: {
							callback: formatChartTick,
						},
					},
					y: {
						type: "linear" as const,
						title: {
							display: true,
							text: "Total Dynamic Head (ft)",
						},
						min: 0,
						ticks: {
							callback: formatChartTick,
						},
					},
				},
			},
		};
	}, [series, systemCurvePoints, dutyGpm, dutyTdh, hiddenSeries]);

	return (
		<div className="pf-pump-curve-chart">
			<Line data={data} options={options} />
		</div>
	);
}
